import Reveal from "@/components/base/Reveal";
import LanguageSwitcher from "@/components/feature/LanguageSwitcher";
import { whatsappLink } from "@/constants/site";

const LANGUAGES = [
  {
    code: "PT",
    label: "Português",
    description: "A base do seu site, com o conteúdo principal para o público brasileiro.",
  },
  {
    code: "EN",
    label: "Inglês",
    description: "Ideal para atender clientes internacionais e parceiros de outros países.",
  },
  {
    code: "ES",
    label: "Espanhol",
    description: "Perfeito para alcançar toda a América Latina com o mesmo projeto.",
  },
];

interface MultilingualSectionProps {
  /** Mensagem do WhatsApp usada no botão de contato. */
  message?: string;
}

export default function MultilingualSection({
  message = "Ola! Quero um site multi-idiomas (portugues, ingles e espanhol) com a NXHOST.",
}: MultilingualSectionProps) {
  return (
    <section className="bg-accent-50/60 py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 md:px-8">
        <Reveal>
          <div className="mx-auto max-w-3xl text-center">
            <span className="inline-flex items-center gap-2 rounded-full bg-accent-100 px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-accent-700">
              <i className="ri-translate-2" aria-hidden="true"></i>
              Sites multi-idiomas
            </span>
            <h2 className="mt-5 font-heading text-2xl font-bold tracking-tight text-foreground-950 md:text-4xl">
              Seu site em português, inglês e espanhol.
            </h2>
            <p className="mt-4 text-base leading-relaxed text-foreground-600">
              Além de um site moderno, criamos também sites <strong>multi-idiomas</strong>. Se a sua
              empresa atende clientes no Brasil e no exterior, entregamos o mesmo projeto em{" "}
              <strong>português, inglês e espanhol</strong>, com a mesma qualidade e experiência.
            </p>
          </div>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-3">
          {LANGUAGES.map((lang, index) => (
            <Reveal key={lang.code} delay={index * 80} className="h-full">
              <article className="flex h-full flex-col items-center rounded-xl border border-accent-200 bg-background-50 p-6 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-accent-100 font-heading text-lg font-bold text-accent-700">
                  {lang.code}
                </span>
                <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">
                  {lang.label}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                  {lang.description}
                </p>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal delay={80}>
          <div className="mt-10 rounded-2xl border border-accent-200 bg-background-50 p-6 md:p-8">
            <LanguageSwitcher />
          </div>
        </Reveal>

        <Reveal delay={120}>
          <div className="mt-10 flex flex-col items-center justify-between gap-5 rounded-xl border border-accent-200 bg-background-50 p-6 text-center sm:flex-row sm:text-left md:p-8">
            <div className="flex items-start gap-4">
              <span className="hidden h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-accent-100 text-accent-700 sm:flex">
                <i className="ri-global-line text-2xl" aria-hidden="true"></i>
              </span>
              <div>
                <p className="font-heading text-base font-semibold text-foreground-950">
                  Quer alcançar clientes em outros idiomas?
                </p>
                <p className="mt-1 text-sm leading-relaxed text-foreground-600">
                  Preparamos o seu site em português, inglês e espanhol, pronto para atender dentro e
                  fora do Brasil.
                </p>
              </div>
            </div>
            <a
              href={whatsappLink(message)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
            >
              Quero um site multi-idiomas
              <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}